"use client";

import { Button } from "../../../../shadcn/ui/button";
import { Label } from "../../../../shadcn/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../../../shadcn/ui/dialog";
import { ArrowLeft, AlertCircle, Star, Plus, RotateCcw, AlertTriangle } from "lucide-react";
import type { ListingData } from "./index";

import { useState, useEffect } from "react";
import policyManagementService from "../../../../services/policyManagementService";
import amenitiesService from "../../../../services/amenitiesService";
import addonService from "../../../../services/addonService";
import SaveStatusIndicator from "./SaveStatusIndicator";
import { useUserCurrency } from "../../../../hooks/useUserCurrency";
import { isAddonDurationCompatible } from "../../../../utils/addonPricingUtils";
import logger from "../../../../utils/logger";


interface StepThreeProps {
  data: ListingData;
  updateData: (data: Partial<ListingData>) => void;
  onNext: () => void;
  onPrev: () => void;
  lastSaved: Date | null;
  onClearForm: () => void;
  isEditMode?: boolean;
}

export default function StepThree({
  data,
  updateData,
  onNext,
  onPrev,
  lastSaved,
  onClearForm,
  isEditMode,
}: StepThreeProps) {
  const [policies, setPolicies] = useState<any[]>([]);
  const [loadingPolicies, setLoadingPolicies] = useState(true);

  const [terms, setTerms] = useState<Array<{ id?: number; title: string; content: string; active: boolean }>>([]);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<{
    id?: number;
    title: string;
    content: string;
    active: boolean;
  } | null>(null);
  const [privacyPolicies, setPrivacyPolicies] = useState<
    Array<{ id?: number; title: string; content: string; active: boolean }>
  >([]);
  const [loadingPrivacy, setLoadingPrivacy] = useState(false);
  const [selectedPrivacy, setSelectedPrivacy] = useState<{
    id?: number;
    title: string;
    content: string;
    active: boolean;
  } | null>(null);
  const [amenities, setAmenities] = useState<Array<{ id: number; name: string; label?: string; description?: string }>>(
    []
  );
  const [loadingAmenities, setLoadingAmenities] = useState(false);
  const [addons, setAddons] = useState<
    Array<{ id: number; name: string; description?: string; unitPrice?: number; duration?: string }>
  >([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const userCurrencyCode = useUserCurrency();

  const formatPrice = (price: number, duration?: string) => {
    if (!price) return "";
    const durationText = {
      HOURLY: "per hour",
      DAILY: "per day",
      WEEKLY: "per week",
      MONTHLY: "per month",
    };
    const currency = userCurrencyCode || "KES";
    return `${currency} ${Number(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${durationText[duration as keyof typeof durationText] || ""}`;
  };

  useEffect(() => {
    fetchCancellationPolicies();
    fetchTerms();
    fetchAmenities();
    fetchAddons();
    fetchPrivacyPolicies();
  }, []);

  const fetchCancellationPolicies = async () => {
    try {
      setLoadingPolicies(true);
      const response = await policyManagementService.getCancellationPolicies(0, 20);
      if (response?.content) {
        setPolicies(response.content);
      }
    } catch (error) {
      logger.error("Error fetching cancellation policies:", error);
      // Fallback to mock policies if API fails
      setPolicies([
        {
          id: 1,
          name: "Flexible",
          description: "Full refund until 24 hours before the experience",
          hoursBefore: 24,
          refundPercentage: 100,
        },
        {
          id: 2,
          name: "Moderate",
          description: "Full refund until 7 days before, 50% refund until 24 hours before",
          hoursBefore: 168,
          refundPercentage: 50,
        },
        {
          id: 3,
          name: "Strict",
          description: "Full refund until 14 days before, no refund after",
          hoursBefore: 336,
          refundPercentage: 100,
        },
      ]);
    } finally {
      setLoadingPolicies(false);
    }
  };

  const fetchTerms = async () => {
    try {
      setLoadingTerms(true);
      const response = await policyManagementService.getTermsAndConditions(0, 20);
      if (response?.content) {
        setTerms(response.content);
      }
    } catch (error) {
      logger.error("Error fetching terms and conditions:", error);
      setTerms([]);
    } finally {
      setLoadingTerms(false);
    }
  };

  const fetchPrivacyPolicies = async () => {
    try {
      setLoadingPrivacy(true);
      // Assuming privacy policies are fetched similarly to terms
      const response = await policyManagementService.getTermsAndConditions(0, 20);
      if (response?.content) {
        setPrivacyPolicies(response.content);
      }
    } catch (error) {
      logger.error("Error fetching privacy policies:", error);
      setPrivacyPolicies([]);
    } finally {
      setLoadingPrivacy(false);
    }
  };

  const fetchAmenities = async () => {
    try {
      setLoadingAmenities(true);
      const response = await amenitiesService.getAllAmenities("name,asc");
      if (response?.data?.content) {
        setAmenities(response.data.content);
      }
    } catch (error) {
      logger.error("Error fetching amenities:", error);
      setAmenities([]);
    } finally {
      setLoadingAmenities(false);
    }
  };

  const fetchAddons = async () => {
    try {
      setLoadingAddons(true);
      const response = await addonService.getMyOrganizationAddons({ size: 100, categoryId: 3 });
      if (response.status === 200 && response.data) {
        // Handle both { content: [] } and { data: { content: [] } } structures
        const addonsData = response.data.content || response.data.data?.content || [];
        setAddons(addonsData);
      }
    } catch (error) {
      logger.error("Error fetching addons:", error);
      setAddons([]);
    } finally {
      setLoadingAddons(false);
    }
  };

  const isValid = true; // Allow navigation without validation for testing

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="border-b bg-background">
        <div className="mx-auto max-w-4xl px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onPrev}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Details
            </button>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">Step 3 of 4</span>
              <SaveStatusIndicator lastSaved={lastSaved} />
              {!isEditMode && (
                <Button variant="ghost" size="sm" onClick={onClearForm} className="gap-2" {...({} as any)}>
                  <RotateCcw className="h-4 w-4" />
                  Start Over
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="border-b bg-background">
        <div className="mx-auto max-w-4xl px-6 py-6">
          <h1 className="mb-2 text-2xl font-semibold">
            {isEditMode ? `Edit: ${data.title || "Play Service"}` : "Create New Play Service"}
          </h1>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full w-3/4 bg-foreground transition-all" />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Add-ons & Policies</span>
            <span className="text-muted-foreground">75% Complete</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-2xl px-6 py-12">
        <div className="mb-12 text-center">
          <h2 className="mb-2 text-3xl font-semibold">
            {isEditMode ? "Adjust add-ons & policies" : "Enhance your experience"}
          </h2>
          <p className="text-muted-foreground">Add optional services and set clear policies to protect your business</p>
        </div>

        <div className="space-y-8">
          {/* Cancellation Policy */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              <h3 className="font-semibold">Cancellation Policy *</h3>
            </div>
            <div className="space-y-2">
              <Label {...({} as any)}>Select a cancellation policy for this service</Label>
              {loadingPolicies ? (
                <div className="py-4 text-center text-sm text-muted-foreground">Loading policies...</div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {policies.map(policy => (
                    <label
                      key={policy.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors hover:bg-accent ${
                        data.cancellationPolicy === policy.id
                          ? "border-blue-600 bg-blue-50/50 dark:border-blue-500"
                          : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="cancellationPolicy"
                        checked={data.cancellationPolicy === policy.id}
                        onChange={() => updateData({ cancellationPolicy: policy.id })}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">{policy.name}</div>
                        {policy.description && (
                          <div className="text-sm text-muted-foreground">{policy.description}</div>
                        )}
                        <div className="mt-2 flex gap-4 text-xs font-medium text-blue-700 dark:text-blue-400">
                          {policy.refundPercentage > 0 && <span>{policy.refundPercentage}% Refund</span>}
                          {policy.hoursBefore > 0 && <span>Up to {policy.hoursBefore}h before</span>}
                        </div>
                      </div>
                    </label>
                  ))}
                  {policies.length === 0 && !loadingPolicies && (
                    <div className="py-4 text-center">
                      <p className="text-sm text-gray-500">No cancellation policies available</p>
                      <p className="mt-1 text-xs text-gray-400">Create policies in Settings → Policy Management</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Amenities */}

          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <Star className="h-5 w-5" />
              <h3 className="font-semibold">Amenities</h3>
            </div>
            <div className="space-y-2">
              <Label {...({} as any)}>What amenities does your experience include?</Label>
              {loadingAmenities ? (
                <div className="py-4 text-center text-sm text-muted-foreground">Loading amenities...</div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {amenities.map(amenity => (
                    <label
                      key={amenity.id}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={data.amenities?.includes(amenity.id) || false}
                        onChange={e => {
                          const currentAmenities = data.amenities || [];
                          if (e.target.checked) {
                            updateData({ amenities: [...currentAmenities, amenity.id] });
                          } else {
                            updateData({ amenities: currentAmenities.filter(id => id !== amenity.id) });
                          }
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">{amenity.name}</div>
                        {amenity.description && (
                          <div className="text-sm text-muted-foreground">{amenity.description}</div>
                        )}
                      </div>
                    </label>
                  ))}
                  {amenities.length === 0 && !loadingAmenities && (
                    <div className="py-4 text-center">
                      <p className="text-sm text-gray-500">No amenities available</p>
                      <p className="mt-1 text-xs text-gray-400">Add amenities in Settings → Amenities Management</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Add-ons */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                <h3 className="font-semibold">Add-ons</h3>
              </div>
            </div>
            <div className="space-y-2">
              <Label {...({} as any)}>What add-ons can participants purchase?</Label>
              <p className="mb-4 text-xs text-muted-foreground">
                Only add-ons created for the <strong>PLAY</strong> category are shown here.
              </p>
              {loadingAddons ? (
                <div className="py-4 text-center text-sm text-muted-foreground">Loading add-ons...</div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {addons.map(addon => {
                    const isChecked = data.addons?.includes(addon.id) || false;
                    const spaceDuration = data.duration || "HOURLY";
                    const { isCompatible, reason } = isAddonDurationCompatible(addon.duration || "FIXED", spaceDuration);
                    const isDisabled = !isCompatible && !isChecked;

                    return (
                      <div
                        key={addon.id}
                        className={`rounded-lg border p-3 transition-all ${
                          !isCompatible
                            ? "bg-slate-50/70 border-slate-200 opacity-75"
                            : isChecked
                            ? "bg-blue-50/40 border-blue-200"
                            : "hover:bg-accent border-border"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isDisabled}
                            onChange={e => {
                              if (!isCompatible && e.target.checked) return;
                              const currentAddons = data.addons || [];
                              if (e.target.checked) {
                                updateData({ addons: [...currentAddons, addon.id] });
                              } else {
                                updateData({ addons: currentAddons.filter(id => id !== addon.id) });
                              }
                            }}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                                  <span className="font-semibold text-sm">{addon.name}</span>
                                  {addon.duration && (
                                    <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground border">
                                      {addon.duration}
                                    </span>
                                  )}
                                </div>
                                {addon.description && (
                                  <div className="text-xs text-muted-foreground line-clamp-1">{addon.description}</div>
                                )}
                              </div>
                              {addon.unitPrice && (
                                <div className="ml-3 text-right shrink-0">
                                  <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                    {formatPrice(addon.unitPrice, addon.duration)}
                                  </div>
                                </div>
                              )}
                            </div>

                            {!isCompatible && (
                              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-start gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                <span>
                                  <strong>Duration mismatch:</strong> {reason || `Cannot attach ${addon.duration} add-on to a ${spaceDuration} service.`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {addons.length === 0 && !loadingAddons && (
                    <div className="py-4 text-center">
                      <p className="text-sm text-gray-500">No add-ons available for Play Services</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <h3 className="font-semibold">Terms & Conditions *</h3>
            </div>
            <div className="space-y-2">
              <Label {...({} as any)}>Select applicable terms and conditions</Label>
              {loadingTerms ? (
                <div className="py-4 text-center text-sm text-muted-foreground">Loading terms...</div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {terms.map(term => (
                    <label
                      key={term.id || `term-${term.title}`}
                      className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={data.termsIds?.includes(term.id || 0) || false}
                        onChange={e => {
                          const currentTerms = data.termsIds || [];
                          const termId = term.id || 0;
                          if (e.target.checked) {
                            updateData({ termsIds: [...currentTerms, termId] });
                          } else {
                            updateData({ termsIds: currentTerms.filter(id => id !== termId) });
                          }
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-semibold">{term.title}</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">{term.content}</div>
                          </div>
                          <Dialog
                            onOpenChange={open => {
                              if (!open) setSelectedTerm(null);
                            }}
                          >
                            <DialogTrigger asChild>
                              {/* @ts-ignore */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedTerm(term)}
                                className="ml-2 shrink-0"
                                {...({} as any)}
                              >
                                View All
                              </Button>
                            </DialogTrigger>
                            {/* @ts-ignore */}
                            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                              {/* @ts-ignore */}
                              <DialogHeader>
                                {/* @ts-ignore */}
                                <DialogTitle>{selectedTerm?.title}</DialogTitle>
                              </DialogHeader>
                              <div className="mt-4">
                                <div className="whitespace-pre-wrap text-sm text-muted-foreground select-text">
                                  {selectedTerm?.content}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </label>
                  ))}
                  {terms.length === 0 && !loadingTerms && (
                    <div className="py-4 text-center">
                      <p className="text-sm text-gray-500">No terms and conditions available</p>
                      <p className="mt-1 text-xs text-gray-400">Create terms in Settings → Policy Management</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Privacy Policy */}
          <div className="rounded-lg border bg-card p-6">
            <div className="mb-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <h3 className="font-semibold">Privacy Policy *</h3>
            </div>
            <div className="space-y-2">
              <Label {...({} as any)}>Select applicable privacy policies</Label>
              {loadingPrivacy ? (
                <div className="py-4 text-center text-sm text-muted-foreground">Loading privacy policies...</div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {privacyPolicies.map(policy => (
                    <label
                      key={policy.id || `privacy-${policy.title}`}
                      className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={data.privacyIds?.includes(policy.id || 0) || false}
                        onChange={e => {
                          const current = data.privacyIds || [];
                          const pid = policy.id || 0;
                          if (e.target.checked) {
                            updateData({ privacyIds: [...current, pid] });
                          } else {
                            updateData({ privacyIds: current.filter(id => id !== pid) });
                          }
                        }}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-semibold">{policy.title}</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">{policy.content}</div>
                          </div>
                          <Dialog
                            onOpenChange={open => {
                              if (!open) setSelectedPrivacy(null);
                            }}
                          >
                            <DialogTrigger asChild>
                              {/* @ts-ignore */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedPrivacy(policy)}
                                className="ml-2 shrink-0"
                                {...({} as any)}
                              >
                                View All
                              </Button>
                            </DialogTrigger>
                            {/* @ts-ignore */}
                            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                              {/* @ts-ignore */}
                              <DialogHeader>
                                {/* @ts-ignore */}
                                <DialogTitle>{selectedPrivacy?.title}</DialogTitle>
                              </DialogHeader>
                              <div className="mt-4">
                                <div className="whitespace-pre-wrap text-sm text-muted-foreground select-text">
                                  {selectedPrivacy?.content}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </label>
                  ))}
                  {privacyPolicies.length === 0 && !loadingPrivacy && (
                    <div className="py-4 text-center">
                      <p className="text-sm text-gray-500">No privacy policies available</p>
                      <p className="mt-1 text-xs text-gray-400">
                        Create privacy policies in Settings → Policy Management
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex items-center justify-between">
          {/* @ts-ignore */}
          <Button variant="outline" onClick={onPrev} className="gap-2 bg-transparent" {...({} as any)}>
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>
          {/* @ts-ignore */}
          <Button onClick={onNext} disabled={!isValid} size="lg" className="gap-2" {...({} as any)}>
            Review & Create
            <span>→</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
